import { useAuth0 } from "@auth0/auth0-react"
import React, { useState } from "react"
import { Icon, Message } from "semantic-ui-react"
import BifrostButton from "../../../../../../Components/BifrostElementComponent/BifrostButton/BifrostButton"
import BifrostModal from "../../../../../../Components/BifrostPageComponent/BifrostModal/BifrostModal"
import Loader from "../../../../../../Components/BifrostPageComponent/Loader/Loader"
import { usePostUpgradeFirmware } from "../../../../../../Hooks/FetchHooks/PostDataService/usePostUpgradeFirmware"
import { isResellerAdminOrAdmin, isSuperAdmin } from "../../../../../../Utils/util"
import "./UpgradeFirmwareModal.scss"
import UpgradeFirmwareModalContent from "./UpgradeFirmwareModalContent"

const UpgradeFirmwareModal = ({ disabled, device, latestFirmwareVersion, firmwareUpToDate }) => {
	const [openFirmwareUpgradeModal, setOpenFirmwareUpgradeModal] = useState(false)
	const [showErrorMessage, setShowErrorMessage] = useState(false)

	const [version, setVersion] = useState("")
	const [isInvalidVersion, setIsInvalidVersion] = useState(false)
	const [invalidateVersionMessage, setInvalidateVersionMessage] = useState(null)

	/**
	 * Handles the input of the new firmware version
	 * @param {string} version
	 */
	const handleNewVersionInput = (version) => {
		// Trim the version string
		let versionString = version.trim()

		// Check if the version string is "latest"
		if (versionString === "latest") {
			setVersion(versionString)
			setIsInvalidVersion(false)
			setInvalidateVersionMessage(null)
		} else {
			if (validateVersion(versionString)) {
				setVersion(versionString)
				setIsInvalidVersion(false)
				setInvalidateVersionMessage(null)
			} else {
				setIsInvalidVersion(true)
			}
		}
	}

	/**
	 * Validates the new firmware version input
	 * @param {string} versionString
	 */
	const validateVersion = (versionString) => {
		if (!versionString) {
			return true
		}

		// Check if the version string does not starts with "v"
		if (versionString[0] !== "v") {
			setInvalidateVersionMessage("The version string must start with 'v'")
			return false
		}

		// Check if the version string contains a dot
		if (!versionString.includes(".")) {
			setInvalidateVersionMessage("The version string must contain a dot")
			return false
		}

		// Check if the version string contains a space
		if (versionString.includes(" ")) {
			setInvalidateVersionMessage("The version string must not contain a space")
			return false
		}

		// Check if the version string end with a dot
		if (versionString.endsWith(".")) {
			setInvalidateVersionMessage("The version string must not end with a dot")
			return false
		}

		setInvalidateVersionMessage(null)
		return true
	}

	const { isLoading, firmwareUpgradeSuccess, postUpgradeFirmware } = usePostUpgradeFirmware()

	const { user } = useAuth0()

	const submitHandler = (e) => {
		let newVersion = "latest"
		if (e.target.id === "push-button") {
			newVersion = version
		}

		postUpgradeFirmware(device.id, newVersion)
			.then(() => {
				setVersion("")
				setShowErrorMessage(false)
				setOpenFirmwareUpgradeModal(false)
			})
			.catch(() => {
				setShowErrorMessage(true)
				setVersion("")
			})
	}

	const cancelHandler = () => {
		setVersion("")
		setIsInvalidVersion(false)
		setShowErrorMessage(false)
		setOpenFirmwareUpgradeModal(false)
	}

	return (
		<>
			{firmwareUpgradeSuccess ? (
				<Message positive size="mini" className="small-message">
					Device is now updating
				</Message>
			) : (
				<>
					{!firmwareUpToDate && (
						<Message size="mini" className="small-message">
							New firmware version available
						</Message>
					)}

					<BifrostModal
						title={"Update Firmware"}
						open={openFirmwareUpgradeModal}
						setOpen={setOpenFirmwareUpgradeModal}
						trigger={
							(isSuperAdmin(user) || !firmwareUpToDate) && (
								<BifrostButton size="medium" style={{ marginLeft: "10px", marginTop: "1px", marginBottom: "1px" }} disabled={disabled}>
									<Icon name="arrow alternate circle down" style={{ margin: "0px", opacity: "1" }} />
								</BifrostButton>
							)
						}
						closeOnEscape={!isLoading}
						closeOnDimmerClick={!isLoading}
						modalPage={
							<>
								{isLoading ? (
									<Loader backgroundColor="white" inverted height="1px" minHeight="50px" />
								) : (
									<UpgradeFirmwareModalContent
										setVersion={handleNewVersionInput}
										isInvalidVersion={isInvalidVersion}
										invalidateVersionMessage={invalidateVersionMessage}
										device={device}
										showErrorMessage={showErrorMessage}
										latestFirmwareVersion={latestFirmwareVersion}
									/>
								)}
							</>
						}
						modalActions={
							<div style={{ display: "flex", justifyContent: "right" }}>
								{isSuperAdmin(user) && (
									<BifrostButton disabled={isLoading || isInvalidVersion} id="push-button" onClick={(e) => submitHandler(e)}>
										Push
									</BifrostButton>
								)}
								{isResellerAdminOrAdmin(user) && (
									<BifrostButton disabled={isLoading} id="confirm-button" onClick={(e) => submitHandler(e)}>
										Confirm
									</BifrostButton>
								)}
								<BifrostButton disabled={isLoading} color="gray" onClick={() => cancelHandler()}>
									Cancel
								</BifrostButton>
							</div>
						}
					/>
				</>
			)}
		</>
	)
}

export default UpgradeFirmwareModal
